import { useState, useEffect } from 'react';
import api from '../../utils/api';

const STATUS_COLORS = {
  pending:  { bg: '#f1f5f9', text: '#475569' },
  trial:    { bg: '#fef3c7', text: '#92400e' },
  expired:  { bg: '#fee2e2', text: '#991b1b' },
  active:   { bg: '#dcfce7', text: '#166534' },
  other:    { bg: '#f1f5f9', text: '#64748b' },
};

export default function AdminSignups() {
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    api.get('/admin/signups')
      .then(d => setSignups(d.signups || []))
      .catch(e => setError(e.error || 'Veri alınamadı'))
      .finally(() => setLoading(false));
  }, []);

  const now = new Date();

  const filtered = signups.filter(s => {
    const matchSearch = !search ||
      s.name?.toLowerCase().includes(search.toLowerCase()) ||
      s.email?.toLowerCase().includes(search.toLowerCase()) ||
      s.phone?.includes(search);
    const matchFilter =
      filter === 'all' ||
      (filter === 'completed' && s.emailVerified && s.phoneVerified) ||
      (filter === 'pending' && s.statusType === 'pending') ||
      (filter === 'trial' && s.statusType === 'trial') ||
      (filter === 'expired' && s.statusType === 'expired');
    return matchSearch && matchFilter;
  });

  const counts = {
    all: signups.length,
    completed: signups.filter(s => s.emailVerified && s.phoneVerified).length,
    pending: signups.filter(s => s.statusType === 'pending').length,
    trial: signups.filter(s => s.statusType === 'trial').length,
    expired: signups.filter(s => s.statusType === 'expired').length,
  };

  const thisWeek = signups.filter(s => new Date(s.createdAt) > new Date(Date.now() - 7 * 86400000)).length;

  if (loading) return <div style={{ padding: 40, color: '#64748b' }}>Yükleniyor...</div>;
  if (error) return <div style={{ padding: 40, color: '#ef4444' }}>Hata: {error}</div>;

  return (
    <div style={{ padding: 32 }}>
      <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>Kayıtlar</h1>
      <p style={{ color: '#64748b', fontSize: 14, marginBottom: 24 }}>Tüm kayıt denemeleri — tamamlanan ve bekleyenler dahil</p>

      {/* Özet kartlar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        {[
          { label: 'Toplam', value: counts.all, color: '#6366f1' },
          { label: 'Aktif Deneme', value: counts.trial, color: '#f59e0b' },
          { label: 'Bu Hafta', value: thisWeek, color: '#10b981' },
          { label: 'Doğrulama Bekliyor', value: counts.pending, color: '#94a3b8' },
          { label: 'Süresi Doldu', value: counts.expired, color: '#ef4444' },
        ].map(({ label, value, color }) => (
          <div key={label} style={{
            background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
            padding: '14px 20px', minWidth: 110,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          }}>
            <div style={{ fontSize: 24, fontWeight: 800, color }}>{value}</div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Filtreler + Arama */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Ara..."
          style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, width: 220, outline: 'none' }}
        />
        <div style={{ display: 'flex', gap: 4 }}>
          {[
            { key: 'all', label: 'Tümü' },
            { key: 'completed', label: 'Tamamlanan' },
            { key: 'pending', label: 'Bekliyor' },
            { key: 'trial', label: 'Deneme' },
            { key: 'expired', label: 'Süresi Doldu' },
          ].map(({ key, label }) => (
            <button key={key} onClick={() => setFilter(key)} style={{
              padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              border: '1px solid', transition: 'all 0.1s',
              borderColor: filter === key ? '#6366f1' : '#e2e8f0',
              background: filter === key ? '#6366f1' : '#fff',
              color: filter === key ? '#fff' : '#64748b',
            }}>
              {label} ({counts[key]})
            </button>
          ))}
        </div>
      </div>

      {/* Tablo */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              {['Otel', 'İletişim', 'Kayıt Tarihi', 'Doğrulama', 'Kaynak', 'Durum'].map(h => (
                <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontWeight: 700, color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>Kayıt bulunamadı.</td>
              </tr>
            ) : filtered.map((s, i) => {
              const sc = STATUS_COLORS[s.statusType] || STATUS_COLORS.other;
              return (
                <tr key={s.id} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <td style={{ padding: '11px 14px' }}>
                    <div style={{ fontWeight: 700, color: '#1e293b' }}>{s.name}</div>
                    {s.ownerName && <div style={{ fontSize: 11, color: '#94a3b8' }}>{s.ownerName}</div>}
                  </td>
                  <td style={{ padding: '11px 14px' }}>
                    <div style={{ color: '#475569' }}>{s.email || '—'}</div>
                    {s.phone && <div style={{ fontSize: 11, color: '#94a3b8' }}>{s.phone}</div>}
                  </td>
                  <td style={{ padding: '11px 14px', color: '#64748b' }}>
                    <div>{new Date(s.createdAt).toLocaleDateString('tr-TR')}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{new Date(s.createdAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</div>
                  </td>
                  <td style={{ padding: '11px 14px' }}>
                    <div style={{ display: 'flex', gap: 4, flexDirection: 'column' }}>
                      <span style={{ fontSize: 11 }}>
                        {s.emailVerified ? '✅ E-posta' : '⬜ E-posta'}
                      </span>
                      <span style={{ fontSize: 11 }}>
                        {s.phoneVerified ? '✅ Telefon' : '⬜ Telefon'}
                      </span>
                    </div>
                  </td>
                  <td style={{ padding: '11px 14px' }}>
                    <span style={{ fontSize: 11, color: '#64748b', textTransform: 'capitalize' }}>{s.signupSource}</span>
                  </td>
                  <td style={{ padding: '11px 14px' }}>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                      background: sc.bg, color: sc.text,
                    }}>
                      {s.statusLabel}
                    </span>
                    {s.trialEndsAt && s.statusType !== 'expired' && (
                      <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                        {new Date(s.trialEndsAt).toLocaleDateString('tr-TR')} bitiş
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p style={{ marginTop: 10, fontSize: 11, color: '#94a3b8' }}>{filtered.length} kayıt</p>
    </div>
  );
}
